import { connect, useDispatch } from "react-redux";
import { useState, useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";
import { CopilotSidebar } from "@copilotkit/react-ui";
import "@copilotkit/react-ui/styles.css";
// TODO: VL-6033 - re-write the vl_chat FE by FE engineers to:
// fe/clustplorer/src/views/components/CopilotCustomComponents/CustomAssistantMessage.tsx
// fe/clustplorer/src/views/components/CopilotCustomComponents/CustomUserMessage.tsx
import { CustomUserMessage, CustomAssistantMessage } from "views/components/CopilotCustomComponents";

import { State } from "redux/store";
import { fetchHealthStatus } from "redux/Datasets/actions";
import { isCartEnabled, isSharingEnabled } from "redux/SingleDataset/selectors";
import { isAnonymousUser, selectUser, userLogout } from "redux/authReducer";
import { getUserInfo, getIsVLChatEnabled } from "redux/User/selectors";

import SelectPlanModal from "views/modals/SelectPlanModal";
import ToastWrapper from "views/components/ToastWrapper";
import Cart from "views/components/Cart";
// TODO: VL-6033 - re-write the vl_chat FE by FE engineers to:
// fe/clustplorer/src/views/components/NewChatButton/style.module.scss
import NewChatButton from "views/components/NewChatButton";

import Loader from "views/uikit/Loader";
import Sidebar from "./Sidebar";
import ReactGA from "react-ga4";
import { SavedView, UserInfo } from "types";

import {
  INVITATION_TOKEN_PARAMETER,
  SAVED_VIEW_PARAMETER,
} from "helpers/constants/miscellaneous";
import { isFastdupUser } from "helpers/utility/utilities";
import { PUBLIC_DATASETS_PATH } from "helpers/constants/paths";

import styles from "./style.module.scss";
import { getIsThemeToggleEnabled } from "redux/Datasets/selectors";
import DatasetSharingMenu from "views/components/SharingMenu/DatasetSharingMenu";
import { acceptInvitation, fetchSavedViews } from "redux/SingleDataset/actions";
import { amplitudeTrack } from "helpers/utility/amplitude";
import useNavigateToView from "hooks/useNavigateToView";
import {
  AMP_DATASET_SHARE_INVITE_ACCEPT,
  AMP_USER_SIGN_IN,
  AMP_USER_SIGN_UP,
} from "helpers/constants/amplitudeEvents";
import {
  AMP_NEW_USER,
  AMP_USER_ORIGIN,
} from "helpers/constants/amplitudeProperties";
import {
  LinkedInConversionId,
  trackLinkedInConversion,
} from "helpers/utility/linkedinAnalytics";
import { THEME } from "views/components/ThemeToggle";
import { getShowBugReportModal } from "redux/Modals/selectors";

function Layout({
  isCartEnabled,
  userAvatar,
  userInfo,
  isSharingEnabled,
  isThemeToggleEnabled,
  showBugReporter,
  isVLChatEnabled,
}: LayoutTypes) {
  const [loading, setLoading] = useState(true);
  
  // Sidebar state management with sessionStorage persistence
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    return sessionStorage.getItem('copilot_sidebar_open') === 'true';
  });
  
  const handleSidebarStateChange = (open: boolean) => {
    setSidebarOpen(open);
    sessionStorage.setItem('copilot_sidebar_open', open.toString());
  };
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { datasetId } = useParams();
  const navigateToView = useNavigateToView();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const isPublicPath = location.pathname === PUBLIC_DATASETS_PATH;

  const navigateToViewIfSavedViewParameter = async () => {
    if (searchParams.get(SAVED_VIEW_PARAMETER)) {
      const views = await dispatch(fetchSavedViews(datasetId) as any);
      if (views && views.length > 0) {
        const view = views.find(
          (view: SavedView) =>
            view.id === searchParams.get(SAVED_VIEW_PARAMETER)
        );
        navigateToView(view);
      }
    }
  };

  const grantAccessForInvite = async () => {
    const invitationToken = searchParams.get(INVITATION_TOKEN_PARAMETER);
    if (datasetId && invitationToken) {
      const user = selectUser();
      if ((user && isAnonymousUser()) || !user) {
        // If user is not logged in we can't grant them access, so we prompt them to sign in.
        userLogout();
      }
      await dispatch(acceptInvitation(datasetId, invitationToken) as any);
      amplitudeTrack(AMP_DATASET_SHARE_INVITE_ACCEPT, {
        [AMP_NEW_USER]: searchParams.get("nuse") === "1",
      });
      searchParams.delete(INVITATION_TOKEN_PARAMETER);
      navigate({ search: `?${searchParams.toString()}` }, { replace: true });
    }
  };

  const reportAnalytics = () => {
    if (searchParams.get("lin") === "1") {
      let user = selectUser();
      amplitudeTrack(AMP_USER_SIGN_IN, {
        [AMP_USER_ORIGIN]: user?.origin,
      });
      searchParams.delete("lin");
    }
    if (searchParams.get("nuse") === "1") {
      amplitudeTrack(AMP_USER_SIGN_UP);
      trackLinkedInConversion(LinkedInConversionId.SignUp);
      ReactGA.event({ category: "User", action: "Sign Up" });
      searchParams.delete("nuse");
    }
    navigate({ search: `?${searchParams.toString()}` });
  };

  useEffect(() => {
    const initializeLayout = async () => {
      reportAnalytics();
      await grantAccessForInvite();
      console.log("datasetId", datasetId);
      console.log("process.env.REACT_APP_IS_AUTHENTICATION_DISABELED", process.env.REACT_APP_IS_AUTHENTICATION_DISABELED);
      console.log("isFastdupUser", isFastdupUser());
      console.log("isAnonymousUser", isAnonymousUser());
      console.log("isPublicPath", isPublicPath);
      if (
        !datasetId &&
        isAnonymousUser() &&
        !isPublicPath &&
        process.env.REACT_APP_IS_AUTHENTICATION_DISABELED === "false" &&
        !isFastdupUser()
      ) {
        userLogout();
      } else {
        await navigateToViewIfSavedViewParameter();
      }

      setLoading(false);
    };
    initializeLayout();

    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    const repeatedHealthCheck = setInterval(() => {
      dispatch(fetchHealthStatus() as any);
    }, 60000);

    dispatch(fetchHealthStatus() as any);

    return () => clearInterval(repeatedHealthCheck);
  }, [dispatch]);

  useEffect(() => {
    if (!isThemeToggleEnabled) {
      return;
    }
    const initialSavedTheme =
      (localStorage.getItem("theme") as THEME) || THEME.DARK;

    if (initialSavedTheme === THEME.LIGHT) {
      document.body.setAttribute("data-theme", THEME.LIGHT);
      localStorage.setItem("theme", THEME.LIGHT);
    } else {
      document.body.setAttribute("data-theme", THEME.DARK);
      localStorage.setItem("theme", THEME.DARK);
    }
  }, [isThemeToggleEnabled]);

  if (!loading) {
    return (
      <>
        <ToastWrapper />
        <div className={styles.mainWrapper}>
          <div className={styles.innerWrapper}>
            <Sidebar
              userAvatar={userAvatar}
              showBugReporter={showBugReporter}
            />
            <div className={styles.content}>
              <div className={styles.body}>
                <Outlet />
              </div>
            </div>
          </div>
        </div>
        {isCartEnabled && <Cart />}
        {isSharingEnabled && <DatasetSharingMenu />}
        <SelectPlanModal />
        {/* TODO: VL-6033 - re-write the vl_chat FE by FE engineers to:
            fe/clustplorer/src/views/components/CopilotCustomComponents/CustomAssistantMessage.module.scss
            fe/clustplorer/src/views/components/CopilotCustomComponents/CustomAssistantMessage.tsx
            fe/clustplorer/src/views/components/CopilotCustomComponents/CustomUserMessage.module.scss
            fe/clustplorer/src/views/components/CopilotCustomComponents/CustomUserMessage.tsx
            fe/clustplorer/src/views/components/CopilotCustomComponents/index.ts
            fe/clustplorer/src/views/components/CopilotCustomComponents/utils.ts
            fe/clustplorer/src/views/components/NewChatButton/style.module.scss
        */}
        {datasetId && !isAnonymousUser() && isVLChatEnabled && (
          <>
            <NewChatButton />
            <CopilotSidebar
              defaultOpen={sidebarOpen}
              onSetOpen={handleSidebarStateChange}
              labels={{
                title: "VL Chat Assistant",
                initial: "Ask me anything about your dataset!",
              }}
              UserMessage={(props) => (
                <CustomUserMessage 
                  {...props} 
                  userName={userInfo?.name}
                  userEmail={userInfo?.email}
                />
              )}
              AssistantMessage={CustomAssistantMessage}
            />
          </>
        )}
      </>
    );
  }
  return (
    <div className={styles.loadingPage}>
      <Loader />
    </div>
  );
}

const mapStateToProps = (state: State) => {
  const userInfo = getUserInfo(state);
  return {
    isCartEnabled: isCartEnabled(state),
    userAvatar: userInfo.userAvatar,
    userInfo: userInfo,
    isSharingEnabled: isSharingEnabled(state),
    isThemeToggleEnabled: getIsThemeToggleEnabled(state),
    showBugReporter: getShowBugReportModal(state),
    isVLChatEnabled: getIsVLChatEnabled(state),
  };
};

export default connect(mapStateToProps)(Layout);

type LayoutTypes = {
  userAvatar?: string;
  userInfo: UserInfo;
  isCartEnabled: boolean;
  isSharingEnabled: boolean;
  isThemeToggleEnabled: boolean;
  showBugReporter: boolean;
  isVLChatEnabled: boolean;
};
